import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ShieldCheck, FlaskConical, Factory, Leaf, Beaker } from "lucide-react";
import StudyDialog from "@/components/StudyDialog";
import { api } from "@/lib/api";
import { useCart } from "@/lib/CartContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";

export default function Product() {
  const { handle } = useParams();
  const { addItem } = useCart();
  const [data, setData] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const [subscribe, setSubscribe] = useState(true);
  const [image, setImage] = useState(0);

  useEffect(() => {
    api.product(handle).then((r) => {
      setData(r);
      setVariantId(r.product.variants[1].id); // default 12-pack
    }).catch(() => {});
  }, [handle]);

  const product = data?.product;
  const ingredients = data?.ingredients || [];
  const variant = useMemo(() => product?.variants.find((v) => v.id === variantId), [product, variantId]);

  const price = useMemo(() => {
    if (!variant) return 0;
    return variant.price * (subscribe ? 0.85 : 1);
  }, [variant, subscribe]);

  if (!product) return <div className="max-w-site mx-auto px-6 md:px-10 py-24 label">Loading…</div>;

  const onAdd = async () => {
    await addItem({ product_id: product.id, variant_id: variant.id, quantity: qty, subscribe });
  };

  return (
    <div className="max-w-site mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="product-page">
      <div className="label !text-[10px] mb-6">
        <Link to="/preview/shop" className="underline">Shop</Link> <span className="mx-2 opacity-60">/</span> <span>{product.flavor}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="relative aspect-[5/5] border border-[color:var(--line)] overflow-hidden" style={{ background: product.color_soft }} data-testid="product-hero-image">
            <div className="absolute inset-0" style={{ background: `radial-gradient(60% 55% at 50% 40%, ${product.color}55, transparent 70%)` }} />
            <img src={product.images[image]} alt={product.flavor} className="relative w-full h-full object-contain p-10" />
            <div className="absolute top-4 left-4 right-4 flex justify-between label !text-[10px]">
              <span>{product.flavor}</span><span>2.0 FL OZ · 60 mL</span>
            </div>
          </div>
          <div className="grid grid-cols-3 mt-4 gap-3">
            {product.images.map((src, i) => (
              <button key={src} onClick={() => setImage(i)} className={`aspect-square border ${image === i ? "border-[color:var(--ink)]" : "border-[color:var(--line)]"} bg-[color:var(--bg-soft)] p-2 transition`} data-testid={`product-thumb-${i}`}>
                <img src={src} alt={`${product.flavor} ${i}`} className="w-full h-full object-contain" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 label mb-3">
            <span className="w-2 h-2" style={{ background: product.color }} />
            <span>Daily Performance</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-[-0.02em] leading-[1.0]">{product.title}</h1>
          <div className="editorial text-2xl mt-2 text-[color:var(--ink-2)]">{product.flavor} — {product.tagline.toLowerCase()}</div>

          <div className="mt-5 flex items-center gap-3">
            <div className="text-[11px] flex items-center gap-1">{Array.from({ length: 5 }).map((_, i) => <span key={i}>★</span>)}</div>
            <span className="label !text-[10px]">4.9 · 50 beta tester reviews</span>
          </div>

          <p className="mt-6 text-[15px] leading-relaxed text-[color:var(--ink-2)]">{product.description}</p>

          {/* Purchase plan */}
          <div className="mt-8 space-y-3">
            <button
              onClick={() => setSubscribe(true)}
              className={`w-full border p-4 flex items-center justify-between text-left transition ${subscribe ? "border-[color:var(--ink)] bg-[color:var(--bg-soft)]" : "border-[color:var(--line)]"}`}
              data-testid="purchase-plan-subscribe"
            >
              <div>
                <div className="label !text-[10px] text-[color:var(--accent-kelp)]">Subscribe & Save · 15% off</div>
                <div className="text-sm mt-1">Every 30 days · cancel anytime</div>
              </div>
              <div className="text-right">
                <div className="text-sm line-through text-[color:var(--ink-4)]">${variant?.price.toFixed(2)}</div>
                <div className="font-display text-xl">${(variant?.price * 0.85).toFixed(2)}</div>
              </div>
            </button>
            <button
              onClick={() => setSubscribe(false)}
              className={`w-full border p-4 flex items-center justify-between text-left transition ${!subscribe ? "border-[color:var(--ink)] bg-[color:var(--bg-soft)]" : "border-[color:var(--line)]"}`}
              data-testid="purchase-plan-onetime"
            >
              <div>
                <div className="label !text-[10px]">One-time purchase</div>
                <div className="text-sm mt-1">Free shipping over $50</div>
              </div>
              <div className="font-display text-xl">${variant?.price.toFixed(2)}</div>
            </button>
          </div>

          {/* Pack size */}
          <div className="mt-6">
            <div className="label !text-[10px] mb-3">Pack size</div>
            <div className="grid grid-cols-3 border border-[color:var(--line)]">
              {product.variants.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setVariantId(v.id)}
                  className={`py-4 text-center border-r last:border-r-0 border-[color:var(--line)] ${variantId === v.id ? "bg-[#0f0f10] text-[#faf9f5]" : "bg-[color:var(--bg)] hover:bg-[color:var(--bg-soft)]"} transition`}
                  data-testid={`variant-${v.id}`}
                >
                  <div className="font-display text-lg">{v.title}</div>
                  <div className={`label !text-[10px] mt-1 ${variantId === v.id ? "!text-[#faf9f5]/70" : ""}`}>${(v.price / v.pack_size).toFixed(2)}/shot</div>
                </button>
              ))}
            </div>
          </div>

          {/* Qty + Add */}
          <div className="mt-6 flex items-stretch gap-3">
            <div className="flex items-center border border-[color:var(--line)]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-12 flex items-center justify-center" data-testid="qty-dec"><Minus size={14} /></button>
              <span className="w-10 text-center text-sm">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-10 h-12 flex items-center justify-center" data-testid="qty-inc"><Plus size={14} /></button>
            </div>
            <button
              onClick={onAdd}
              className="flex-1 h-12 bg-[#0f0f10] text-[#faf9f5] label !tracking-[0.25em] hover:bg-[#2a2a2a] transition flex items-center justify-between px-5"
              data-testid="add-to-cart"
            >
              <span>Add to bag</span>
              <span>${(price * qty).toFixed(2)}</span>
            </button>
          </div>

          {/* Benefits mini */}
          <div className="mt-10 grid grid-cols-2 gap-px bg-[color:var(--line)] border border-[color:var(--line)]">
            {[
              { icon: ShieldCheck, t: "3rd Party Tested", s: "EUROFINS" },
              { icon: Factory, t: "cGMP Facility", s: "Manufactured in USA" },
              { icon: FlaskConical, t: "15 Clinical Actives", s: "Branded + verified" },
              { icon: Leaf, t: "0 Sugar", s: "0 Sucralose" },
            ].map((b, i) => (
              <div key={b.t} className="bg-[color:var(--bg)] p-4 flex items-center gap-3" data-testid={`pdp-badge-${i}`}>
                <b.icon size={18} strokeWidth={1.3} />
                <div>
                  <div className="text-sm">{b.t}</div>
                  <div className="label !text-[10px]">{b.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* TABS: Science / Nutrition / How to use */}
      <section className="mt-20 md:mt-28" data-testid="pdp-tabs">
        <SectionLabel index="02 / Details">What's inside.</SectionLabel>
        <Tabs defaultValue="science" className="mt-8">
          <TabsList className="bg-transparent border-b border-[color:var(--line)] w-full justify-start gap-6 rounded-none h-auto p-0">
            <TabsTrigger value="science" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[color:var(--ink)] bg-transparent data-[state=active]:bg-transparent label py-3 !tracking-[0.2em] shadow-none" data-testid="tab-science">Science</TabsTrigger>
            <TabsTrigger value="nutrition" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[color:var(--ink)] bg-transparent data-[state=active]:bg-transparent label py-3 !tracking-[0.2em] shadow-none" data-testid="tab-nutrition">Nutrition</TabsTrigger>
            <TabsTrigger value="how" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[color:var(--ink)] bg-transparent data-[state=active]:bg-transparent label py-3 !tracking-[0.2em] shadow-none" data-testid="tab-how">How to use</TabsTrigger>
          </TabsList>
          <TabsContent value="science" className="pt-8">
            <div className="grid grid-cols-12 border-b border-[color:var(--line)] label !text-[10px] py-3 px-4 bg-[color:var(--bg-soft)]">
              <div className="col-span-5">Ingredient</div>
              <div className="col-span-2">Dose</div>
              <div className="col-span-2 hidden md:block">Category</div>
              <div className="col-span-5 md:col-span-3">Benefit</div>
            </div>
            <div className="border border-t-0 border-[color:var(--line)]">
              {ingredients.map((ing, i) => (
                <div key={ing.name} className="grid grid-cols-12 py-4 px-4 border-b last:border-b-0 border-[color:var(--line)] text-sm">
                  <div className="col-span-5 flex items-center gap-2">
                    <span className="font-mono tabular-nums text-[10px] text-[color:var(--ink-3)]">{String(i + 1).padStart(2, "0")}</span>
                    <span>{ing.name}</span>
                  </div>
                  <div className="col-span-2 text-sm">
                    {ing.show_dose ? (
                      <span className="inline-block font-mono tabular-nums text-[13px] font-semibold px-2 py-0.5 bg-[#a8c69f]/30 text-[#3a4a32] border border-[#a8c69f]/70">{ing.dose}</span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-6 h-6 border border-[color:var(--line)] text-[color:var(--ink-3)]" title="Measured within the Vitura Performance™ Blend">
                        <Beaker size={13} strokeWidth={1.5} />
                      </span>
                    )}
                  </div>
                  <div className="col-span-2 hidden md:block label !text-[10px]">{ing.category}</div>
                  <div className="col-span-5 md:col-span-3 text-[color:var(--ink-2)]">
                    {ing.benefit}
                    <StudyDialog ingredient={ing} />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="nutrition" className="pt-8">
            <div className="max-w-md border-2 border-[color:var(--ink)] p-5 bg-[color:var(--bg)]">
              <div className="font-display text-3xl tracking-tight">Supplement Facts</div>
              <div className="label !text-[10px] mt-1">Serving Size 1 bottle (2 fl oz / 60 mL)</div>
              <div className="h-[6px] bg-[color:var(--ink)] my-3" />
              <div className="flex justify-between label !text-[10px]"><span>Amount per serving</span><span>% Daily Value</span></div>
              <div className="h-px bg-[color:var(--ink)] my-2" />
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span>Calories</span><span>45</span></div>
                <div className="flex justify-between"><span className="pl-3 text-[color:var(--ink-3)]">Calories from Saturated Fat</span><span>0</span></div>
                <div className="flex justify-between"><span>Total Carbohydrate</span><span>12 g · 4%</span></div>
                <div className="flex justify-between"><span className="pl-3 text-[color:var(--ink-3)]">Total Sugars</span><span>0 g · †</span></div>
                <div className="flex justify-between"><span>Vitamin C (as Quali-C®)</span><span>50 mg · 56%</span></div>
                <div className="flex justify-between"><span>Magnesium (as Bisglycinate Chelate)</span><span>25 mg · 1.2%</span></div>
              </div>
              <div className="h-px bg-[color:var(--ink)] my-2" />
              <div className="space-y-1 text-[13px]">
                {ingredients.filter(i => !i.name.startsWith("Vitamin C") && !i.name.startsWith("Magnesium")).map((ing) => (
                  <div key={ing.name} className="flex justify-between"><span>{ing.name}</span>{ing.show_dose ? <span className="font-medium text-[#4a5941]">{ing.dose} · †</span> : <Beaker size={12} strokeWidth={1.5} className="shrink-0 mt-[3px] text-[color:var(--ink-4)]" />}</div>
                ))}
              </div>
              <div className="h-px bg-[color:var(--ink)] my-2" />
              <div className="label !text-[9px] leading-snug">* Percent Daily Value (DV) based on a 2,000 calorie diet. † Daily Value not established.</div>
              <div className="label !text-[9px] leading-snug mt-2">Other ingredients: Water, glycerine, natural flavors, potassium sorbate, sodium benzoate, malic acid, citric acid, monk fruit extract, agave powder.</div>
            </div>
          </TabsContent>
          <TabsContent value="how" className="pt-8 max-w-2xl">
            <ol className="space-y-5 text-[15px] leading-relaxed">
              <li><span className="label !text-[10px] mr-2">01</span>Shake well before opening. Best served chilled.</li>
              <li><span className="label !text-[10px] mr-2">02</span>Drink one bottle in the morning or before work/training.</li>
              <li><span className="label !text-[10px] mr-2">03</span>Effects begin in 15–30 minutes. Long-arc benefits build over 2–3 weeks.</li>
              <li><span className="label !text-[10px] mr-2">04</span>Replace a coffee, pre-workout, or mid-afternoon pick-me-up. Not both at once.</li>
            </ol>
          </TabsContent>
        </Tabs>
      </section>

      {/* Cross-sell */}
      <section className="mt-20 md:mt-28" data-testid="pdp-crosssell">
        <SectionLabel index="03 / Try the other one">The other expression.</SectionLabel>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/preview/shop" className="block border border-[color:var(--line)] p-6 hover:bg-[color:var(--bg-soft)] transition">
            <div className="label !text-[10px]">Explore</div>
            <div className="font-display text-2xl mt-2">See all flavors →</div>
          </Link>
          <Link to="/journal" className="block border border-[color:var(--line)] p-6 hover:bg-[color:var(--bg-soft)] transition">
            <div className="label !text-[10px]">Journal</div>
            <div className="font-display text-2xl mt-2">Read the science →</div>
          </Link>
        </div>
      </section>
    </div>
  );
}
